import React, { PureComponent } from 'react';
import { Flex } from '../../../common/Flex';
import {
    CheckCircleFilled,
    CheckOutlined,
    EditOutlined,
    FileFilled,
    MinusSquareFilled,
    PlusSquareFilled,
    PushpinFilled,
} from '@ant-design/icons';
import { TVLoad } from './TVLoad';
import {
    FlightId,
    HotspotDto,
    LoadClassification,
    MonitoredLoadValue,
    TimePeriod,
    TvRefLocation,
} from 'app/model/CORE';
import Utils from '../../../../common/util/Utils';
import TimePeriodUtils from '../../../../common/util/TimePeriodUtils';
import TimeUtils from '../../../../common/util/TimeUtils';
import {
    ExtendedMonitoredTv,
    MonitoredEvent,
    MonitoredMcdmMeasure,
    MonitoredMeasure,
    TvMeteoAlert,
    TvSetForecastProgress,
    WeatherEvent,
} from 'app/model/SOL49';
import MeasureUtils from '../../../../common/util/MeasureUtils';
import { SortUtils } from 'app/common/util/SortUtils';
import memoizeOne from 'memoize-one';
import './TVSetLoad.less';
import { MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import ConditionalRender from 'app/common/components/ConditionalRender';
import { ServiceFactory } from 'app/service/ServiceFactory';
import AppContext from 'app/common/context/AppContext';

interface TVSetLoadProps {
    tvSet: string;
    progress?: TvSetForecastProgress;
    lastUpdate?: Date;
    monitoredTvs: Array<ExtendedMonitoredTv>;
    currentTime: Date;
    monitoredMeasures?: Array<MonitoredMeasure>;
    pendingMeasureProposals?: Array<MonitoredMcdmMeasure>;
    monitoredEvents?: Array<MonitoredEvent>;
    tvsOccupancyPeriods?: { [key: string]: Map<FlightId, TimePeriod> };
    supervised?: boolean;

    selectedTvs?: ExtendedMonitoredTv[];

    onSelectionChange?(tvIds: ExtendedMonitoredTv[], selected: boolean): void;

    displayOccupancy?: 'all' | 'sustain' | 'peak' | 'none'; // default is 'all'
    displayOccupancyForTv?: 'all' | 'low' | 'high' | 'overload'; // default is 'all'

    meteoAlerts: Array<TvMeteoAlert>; //it has all alerts for all tvs in the tvset

    showLoadInactivePeriods: boolean;
    hotspots?: { [key: string]: Array<HotspotDto> };
}

interface TVSetLoadState {
    collapsed: boolean;
    progress: TvSetForecastProgress;
}

export class TVSetLoad extends PureComponent<TVSetLoadProps, TVSetLoadState> {
    constructor(props) {
        super(props);
        this.state = {
            collapsed: false,
            progress: this.props.progress,
        };
    }

    componentDidUpdate(prevProps: Readonly<TVSetLoadProps>, prevState: Readonly<TVSetLoadState>, snapshot?: any) {
        if (prevProps.progress !== this.props.progress) {
            this.setState({
                progress: this.props.progress,
            });
        }
    }

    private maxLoadClassification = (lc1: LoadClassification, lc2: LoadClassification) => {
        const lc1Value = Object.keys(LoadClassification).indexOf(LoadClassification[lc1]);
        const lc2Value = Object.keys(LoadClassification).indexOf(LoadClassification[lc2]);
        return lc1Value > lc2Value ? lc1 : lc2;
    };

    private getCombinedLoadForPeriod = (period: TimePeriod): LoadClassification => {
        let highestLoad = LoadClassification.UNDEFINED;
        for (let i = 0; i < this.props.monitoredTvs.length; i++) {
            const monitoredTv = this.props.monitoredTvs[i];
            for (let j = 0; j < monitoredTv.loadValues.length; j++) {
                const monitoredLoad = monitoredTv.loadValues[j];
                if (TimePeriodUtils.contains(monitoredLoad.timePeriod, period)) {
                    highestLoad = this.maxLoadClassification(highestLoad, monitoredLoad.loadClassification);
                    break;
                }
            }
        }
        return highestLoad;
    };

    renderFoldedContent = (tvIds: string[]) => {
        // Combine load values of all TVs
        const loadValues = new Array<MonitoredLoadValue>();

        // Start by getting all the times that constitute period splits
        const dates = new Array<Date>();
        for (let i = 0; i < this.props.monitoredTvs.length; i++) {
            const monitoredTv = this.props.monitoredTvs[i];
            for (let j = 0; j < monitoredTv.loadValues.length; j++) {
                const monitoredLoad = monitoredTv.loadValues[j];
                if (!Utils.contains(dates, monitoredLoad.timePeriod.from)) dates.push(monitoredLoad.timePeriod.from);
                if (!Utils.contains(dates, monitoredLoad.timePeriod.to)) dates.push(monitoredLoad.timePeriod.to);
            }
        }
        dates.sort((a, b) => TimeUtils.compare(b, a)); // Order times

        // Prepare structure for monitored load values
        let from = dates[0];
        for (let i = 1; i < dates.length; i++) {
            const period = {
                from: from,
                to: dates[i],
            };
            loadValues.push({
                loadClassification: this.getCombinedLoadForPeriod(period),
                timePeriod: period,
            });
            from = dates[i];
        }

        const monitoredMeasures = this.props.monitoredMeasures?.filter(monitoredMeasure =>
            Utils.contains(tvIds, MeasureUtils.getTvLocation(monitoredMeasure.measure).id),
        );
        const pendingMeasureProposal = this.props.pendingMeasureProposals?.find(proposal =>
            Utils.contains(tvIds, MeasureUtils.getTvLocation(proposal.measure).id),
        );

        return (
            <Flex direction="vertical" className="tvset-content">
                <ConditionalRender condition={AppContext.isForecast()}>
                    <div className={`decorator ${this.state.progress || ''}`} />
                </ConditionalRender>
                <div className="tvs-load">
                    <TVLoad
                        vsName={this.props.monitoredTvs.length + ' TVs'}
                        elementary={false}
                        entryLoad={loadValues}
                        currentTime={this.props.currentTime}
                        monitoredMeasures={monitoredMeasures}
                        pendingMeasureProposal={pendingMeasureProposal}
                        monitorable={true}
                    />
                </div>
            </Flex>
        );
    };

    isSelected = () => {
        return this.props.monitoredTvs.length === this.props.selectedTvs.length;
    };

    handleTVSetSelectionChange = () => {
        const isSelected = this.isSelected();
        this.props.onSelectionChange?.(this.props.monitoredTvs, !isSelected);
    };

    handleSelectionChange = (tv: ExtendedMonitoredTv, selected: boolean) => {
        this.props.onSelectionChange?.([tv], selected);
    };

    getSelectionChangeHandlerByTv = memoizeOne((monitoredTvs: Array<ExtendedMonitoredTv>) => {
        const map = new Map();
        if (monitoredTvs) {
            monitoredTvs.forEach(monitoredTv => {
                map.set(monitoredTv.refLocation.id, selected => this.handleSelectionChange(monitoredTv, selected));
            });
        }
        return map;
    });

    /**
     * Returns the alerts just for the given tv
     */
    getTvMeteoAlerts = memoizeOne((tvSetMeteoAlerts: Array<TvMeteoAlert>, tvName: string) => {
        if (!tvSetMeteoAlerts) return;
        return tvSetMeteoAlerts.filter(tvAlert => tvAlert.tvName === tvName);
    });

    getMonitoredMeasuresForTv = memoizeOne((monitoredMeasures: Array<MonitoredMeasure>, tvId: string) => {
        return monitoredMeasures?.filter(
            monitoredMeasure => MeasureUtils.getTvLocation(monitoredMeasure.measure).id === tvId,
        );
    });

    getMonitoredEventsForTv = memoizeOne((events: MonitoredEvent[], mtv: ExtendedMonitoredTv) => {
        return this.props.monitoredEvents?.filter(
            e =>
                (MonitoredEventUtils.isWeatherEvent(e) &&
                    e.refLocs?.some(refLoc => Utils.isShallowEquals(refLoc, mtv.tvRefLocation))) ||
                (MonitoredEventUtils.isNoteEvent(e) && e.tvs?.includes(mtv.refLocation.id)),
        );
    });

    renderUnfoldedContent = () => {
        const selectionChangeHandlerByTv = this.getSelectionChangeHandlerByTv(this.props.monitoredTvs);
        return (
            <Flex className="tvset-content" direction="vertical">
                <div className={`decorator ${AppContext.isForecast() ? this.state.progress || '' : ''}`} />
                <div className="tvs-load">
                    {this.props.monitoredTvs?.sort(SortUtils.MONITORED_TV_ALPHABETICAL_SORTING).map(monitoredTv => {
                        const selected = this.props.selectedTvs?.indexOf(monitoredTv) >= 0;
                        const tvId = monitoredTv.refLocation.id;
                        const occupancyPeriods = this.props.tvsOccupancyPeriods?.[tvId];
                        const pendingMeasureProposal = this.props.pendingMeasureProposals?.find(
                            proposal => MeasureUtils.getTvLocation(proposal.measure).id === tvId,
                        );
                        const hotspots = this.props.hotspots[tvId];
                        return (
                            <TVLoad
                                key={this.props.tvSet + '-' + tvId}
                                tvName={tvId}
                                vsName={this.props.tvSet}
                                hideVsName={true}
                                refLoc={monitoredTv.tvRefLocation}
                                entryLoad={monitoredTv.loadValues}
                                elementary={monitoredTv.elementary}
                                occupancyLoad={monitoredTv.occupancyLoadValues}
                                currentTime={this.props.currentTime}
                                monitoredMeasures={this.getMonitoredMeasuresForTv(this.props.monitoredMeasures, tvId)}
                                pendingMeasureProposal={pendingMeasureProposal}
                                activePeriods={monitoredTv.activePeriods}
                                showLoadInactivePeriods={this.props.showLoadInactivePeriods}
                                occupancyPeriods={occupancyPeriods}
                                monitorable={monitoredTv.monitorable}
                                selected={selected}
                                onSelectionChange={selectionChangeHandlerByTv.get(tvId)}
                                displayOccupancy={this.props.displayOccupancy}
                                displayOccupancyForTv={this.props.displayOccupancyForTv}
                                tvMeteoAlerts={this.getTvMeteoAlerts(this.props.meteoAlerts, tvId)}
                                events={this.getMonitoredEventsForTv(this.props.monitoredEvents, monitoredTv)}
                                hotspots={hotspots}
                            />
                        );
                    })}
                </div>
            </Flex>
        );
    };

    setCollapsed = (collapsed: boolean) => {
        this.setState({ collapsed: collapsed });
    };

    private hasNoteEvent = memoizeOne(
        (event: MonitoredEvent, tvSet: string, tvs: string[]) =>
            MonitoredEventUtils.isNoteEvent(event) &&
            (event.fmps?.some(fmp => fmp === tvSet) || event.tvs?.some(tv => tvs.includes(tv))),
    );
    private hasWeatherEvent = memoizeOne(
        (event: WeatherEvent, refLocs: TvRefLocation[]) =>
            MonitoredEventUtils.isWeatherEvent(event) &&
            event.refLocs?.some(refLoc => refLocs.some(rl => Utils.isShallowEquals(refLoc, rl))),
    );

    render() {
        const className = 'tvset-load on-hover' + (this.props.supervised ? ' supervised' : '');
        const headerClassName = 'tvset-header on-hover' + (this.isSelected() ? ' selected' : '');
        const selectorClassName = 'selector check-circle' + (!this.isSelected() ? ' show-on-hover' : '');
        const tvIds = this.props.monitoredTvs.map(monitoredTv => monitoredTv.refLocation.id);
        const refLocs = this.props.monitoredTvs.map(monitoredTv => monitoredTv.tvRefLocation);
        const monitoredEvents = this.props.monitoredEvents?.filter(
            e => this.hasWeatherEvent(e, refLocs) || this.hasNoteEvent(e, this.props.tvSet, tvIds),
        );
        return (
            <Flex direction="vertical" className={className}>
                <Flex className={headerClassName}>
                    <Flex>
                        <div className={selectorClassName}>
                            <CheckCircleFilled onClick={this.handleTVSetSelectionChange} />
                        </div>
                        <div className="title">{this.props.tvSet}</div>
                        <div className="pin">
                            <PushpinFilled />
                        </div>
                        <ConditionalRender
                            condition={AppContext.isForecast() && this.state.progress !== TvSetForecastProgress.DONE}>
                            <EditOutlined className="show-on-hover" onClick={this.updateTvSetProgress} />
                        </ConditionalRender>
                        <ConditionalRender
                            condition={AppContext.isForecast() && this.state.progress === TvSetForecastProgress.DONE}>
                            <CheckOutlined className="show-on-hover" onClick={this.updateTvSetProgress} />
                        </ConditionalRender>
                        <ConditionalRender condition={Utils.isNotEmpty(monitoredEvents)}>
                            <FileFilled />
                        </ConditionalRender>
                        <ConditionalRender condition={Utils.isExists(this.props.lastUpdate)}>
                            <div className={'last-update show-on-hover'}>
                                {TimeUtils.formatmmDDHHMM(this.props.lastUpdate)}
                            </div>
                        </ConditionalRender>
                    </Flex>
                    <Flex className="actions" grow={false}>
                        {this.state.collapsed ? (
                            <PlusSquareFilled onClick={() => this.setState({ collapsed: false })} />
                        ) : (
                            <MinusSquareFilled onClick={() => this.setState({ collapsed: true })} />
                        )}
                    </Flex>
                </Flex>
                {this.state.collapsed ? this.renderFoldedContent(tvIds) : this.renderUnfoldedContent()}
            </Flex>
        );
    }

    private updateTvSetProgress = () => {
        const newProgress =
            this.state.progress === TvSetForecastProgress.WIP ? TvSetForecastProgress.DONE : TvSetForecastProgress.WIP;
        this.setState(
            {
                progress: newProgress,
            },
            () =>
                ServiceFactory.sol49Service().updatetvSetForecastProgress(
                    AppContext.getMonitoredDay().getTime(),
                    this.props.tvSet,
                    newProgress,
                ),
        );
    };
}
